import { useAuthStoreShallow } from "@/features/authentication";
import type { TUser } from "@/shared/services";
import { cn } from "@lib/cn";
import {
  ANALYTICS_QUERY,
  VERSIONS_TABLE_SIZES,
} from "@pages/analytics/constants";
import { useAnalyticsQuery } from "@pages/analytics/hooks";
import type { ReaderType, VersionType } from "@pages/analytics/types";
import { openDialog } from "@store/dialogs";
import { useAppDispatch } from "@store/store";
import {
  IconExternalLink,
  IconMinus,
  IconPlus,
  IconUserQuestion,
} from "@tabler/icons-react";
import moment from "moment";
import { useCallback, useState } from "react";
import { AreYouSureDialog } from "../are-you-sure-dialog";
import { DeleteVersionLockDialog } from "../delete-version-lock-dialog";
import { VersionDetails } from "../version-details/version-details";
import { VersionTableSessionRowSkeleton } from "./version-table-session-row-skeleton";
import { VersionsRowActions } from "./versions-row-actions";
import { VersionsTableSessionRow } from "./versions-table-session-row";
import { VersionsTableSessionsHeader } from "./versions-table-sessions-header";

export const VersionsTableRow = ({
  visitors,
  session_avg_time,
  version_title,
  page_title,
  creator_id,
  version_id,
  story_id,
  event_url,
  created_at,
  avg_completion_level,
  clicked_cta_percentage,
  users,
  floatingAction,
}: VersionType & { users: TUser[]; floatingAction?: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: response, isLoading } = useAnalyticsQuery({
    query: [ANALYTICS_QUERY.READERS],
    overrides: {
      versionId: version_id,
      storyId: story_id,
    },
    enabled: isExpanded,
    skipDebounce: true,
  });

  const { isUserOrCreatorLite } = useAuthStoreShallow((s) => {
    return {
      isUserOrCreatorLite: s.isUserOrCreatorLite,
    };
  });

  const sessions = response?.[ANALYTICS_QUERY.READERS] as ReaderType[];
  const isUnread = visitors === 0;

  const owner = users.find((user) => user.oktaId === creator_id);
  const isOwner = owner?.oktaId === creator_id;

  const getInitials = (name?: string) => {
    if (!name) return "";
    const words = name.split(" ").filter(Boolean);
    return words
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  const openVersionDetails = useCallback(() => {
    if (isUnread) return;
    dispatch(
      openDialog({
        id: VersionDetails.id,
        content: VersionDetails,
        props: {
          version_title,
          page_title,
          version_id,
          story_id,
          created_at,
          owner,
          isOwner,
          className: "tw-w-full",
        },
      }),
    );
  }, [
    dispatch,
    version_title,
    page_title,
    version_id,
    story_id,
    created_at,
    owner,
    isOwner,
    isUnread,
  ]);

  const deleteSessions = useCallback(() => {
    if (isUnread) return;
    if (isUserOrCreatorLite && !isOwner) {
      dispatch(
        openDialog({
          id: DeleteVersionLockDialog.id,
          content: DeleteVersionLockDialog,
          props: {
            type: "version",
          },
        }),
      );
      return;
    }
    dispatch(
      openDialog({
        id: AreYouSureDialog.id,
        content: AreYouSureDialog,
        props: {
          versionId: version_id,
        },
      }),
    );
  }, [dispatch, isUnread, isUserOrCreatorLite, isOwner, version_id]);

  return (
    <>
      <div className="tw-flex tw-items-center tw-bg-white hover:tw-bg-blue-50/20 tw-group/row tw-border-b tw-border-solid tw-border-blue-100 tw-py-0.5 tw-h-16 hover:tw-bg-blue-25 tw-transition-colors group/row last:tw-border-b-0">
        {/* Name column */}
        <div className="tw-flex-auto tw-px-2 tw-pl-4 tw-py-3 tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-w-80 tw-shrink-0 tw-mr-auto -2xl:tw-w-52 -md:tw-w-60">
          <button
            type="button"
            onClick={() => {
              if (isUnread) return;
              setIsExpanded((prev) => !prev);
            }}
            className={cn(
              "tw-size-6 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-shadow-2xs hover:tw-bg-blue-50 tw-transition-colors tw-flex-shrink-0",
              isUnread && "tw-cursor-not-allowed",
            )}
          >
            {isExpanded ? (
              <IconMinus className="tw-size-3.5 tw-text-blue" />
            ) : (
              <IconPlus className="tw-size-3.5 tw-text-blue" />
            )}
          </button>
          <div className="tw-flex tw-flex-col tw-gap-1 tw-min-w-0">
            <div className="tw-flex tw-items-center tw-gap-2">
              <p
                role="button"
                tabIndex={0}
                className="tw-font-sans tw-font-medium tw-text-sm tw-text-blue tw-truncate tw-cursor-pointer"
                onClick={openVersionDetails}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openVersionDetails(); }}
              >
                {version_title}
              </p>
              <a
                href={`${event_url}?preview=true`}
                target="_blank"
                rel="noreferrer"
                className="tw-size-4 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-flex-shrink-0 tw-group/link"
              >
                <IconExternalLink className="tw-size-3.5 tw-text-blue-500 group-hover/link:tw-text-primary tw-transition-colors" />
              </a>
            </div>
            {page_title && (
              <p className="tw-font-sans tw-font-normal tw-text-xs tw-text-blue-500 tw-truncate tw-leading-4">
                {page_title}
              </p>
            )}
          </div>
        </div>

        {/* Readers column */}
        <div
          className="tw-px-2 tw-flex tw-items-center tw-h-16"
          style={{ width: VERSIONS_TABLE_SIZES.READERS }}
        >
          <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500">
            {visitors}
          </p>
        </div>

        {/* Avg reading time column */}
        <div
          className="tw-px-2 tw-flex tw-items-center tw-h-16"
          style={{ width: VERSIONS_TABLE_SIZES.AVG_READING_TIME }}
        >
          <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500">
            {isUnread ? "--:--" : session_avg_time}
          </p>
        </div>

        {/* Engaged users column */}
        <div
          className="tw-px-2 tw-flex tw-items-center tw-h-16"
          style={{ width: VERSIONS_TABLE_SIZES.ENGAGED_USERS }}
        >
          <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500">
            {isUnread ? "--" : clicked_cta_percentage}%
          </p>
        </div>

        {/* Avg progress column */}
        <div
          className="tw-px-2 tw-flex tw-items-center tw-gap-2 tw-h-16"
          style={{ width: VERSIONS_TABLE_SIZES.AVG_PROGRESS }}
        >
          <div className="tw-size-6 tw-flex tw-items-center tw-justify-center">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              className="tw-transform -tw-rotate-90"
              aria-label={`${avg_completion_level}% progress`}
            >
              <title>{avg_completion_level}% progress</title>
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="#E7E6EC"
                strokeWidth="3"
              />
              {avg_completion_level && (
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="#610CFF"
                  strokeWidth="3"
                  strokeDasharray={`${avg_completion_level * 0.628} 62.8`}
                  strokeLinecap="round"
                />
              )}
            </svg>
          </div>
          <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500">
            {isUnread ? "--" : avg_completion_level}%
          </p>
        </div>

        {/* Created at column */}
        <div
          className="tw-px-2 tw-flex tw-items-center tw-h-16"
          style={{ width: VERSIONS_TABLE_SIZES.CREATED_AT }}
        >
          <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500">
            {moment.utc(created_at).local().format("D MMM · h:mm A")}
          </p>
        </div>

        {/* Owner column */}
        <div
          className="tw-relative tw-flex tw-items-center tw-gap-3 tw-h-16"
          style={{ width: VERSIONS_TABLE_SIZES.OWNER }}
        >
          {floatingAction}
          <div className="tw-px-2 tw-w-12 tw-flex tw-items-center tw-justify-end">
            <div className="tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-blue/10 tw-flex tw-items-center tw-justify-center tw-bg-blue-50 tw-overflow-hidden tw-text-xs tw-text-blue-500 tw-font-sans">
              {!owner && (
                <IconUserQuestion
                  className="tw-size-4 tw-text-blue/50"
                  stroke={2}
                />
              )}
              {owner?.firstName && owner?.profile?.picture ? (
                <img
                  src={owner?.profile?.picture}
                  alt={owner?.firstName}
                  className="tw-size-full tw-object-cover"
                />
              ) : (
                owner?.firstName && getInitials(owner?.firstName)
              )}
            </div>
          </div>
          <div
            className={cn(
              "tw-size-8 tw-flex tw-items-center tw-justify-center",
              isUnread && "tw-cursor-not-allowed tw-pointer-events-none",
            )}
          >
            <VersionsRowActions
              onVersionDetails={openVersionDetails}
              onDeleteSessions={deleteSessions}
            />
          </div>
        </div>
      </div>
      {isExpanded && (
        <div>
          <VersionsTableSessionsHeader />
          {isLoading
            ? Array.from({ length: 7 }).map((_, index) => (
                <VersionTableSessionRowSkeleton key={index} />
              ))
            : sessions?.map((session: ReaderType) => (
                <VersionsTableSessionRow
                  key={session.session_id}
                  session={session}
                  pageTitle={page_title}
                  versionTitle={version_title}
                  storyId={story_id}
                  versionId={version_id}
                  allSessions={sessions}
                  isOwner={isOwner}
                />
              ))}
        </div>
      )}
    </>
  );
};
